import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * BalanceSheetContext — global state for the balance-sheet balance check.
 *
 * The Dashboard computes the full balance sheet (Assets vs Liabilities + Equity) and
 * publishes the resulting {isBalanced, difference, periodId, updatedAt} here. Other pages
 * subscribe and render the global banner if the sheet is unbalanced.
 *
 * Persistence: the last published value is stored in localStorage so the banner survives
 * page reloads / direct navigation to non-Dashboard pages.
 */

const STORAGE_KEY = 'balance_sheet_status_v1';

const defaultState = {
  // null until first published; the banner only renders when isBalanced === false
  isBalanced: null,
  difference: 0,
  periodId: null,
  updatedAt: null,
};

const readFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.isBalanced === 'boolean') return { ...defaultState, ...parsed };
    return defaultState;
  } catch (e) {
    return defaultState;
  }
};

const writeToStorage = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // ignore quota / privacy mode errors
  }
};

const BalanceSheetContext = createContext({
  ...defaultState,
  publishBalance: () => {},
  clearBalance: () => {},
});

export const BalanceSheetProvider = ({ children }) => {
  const [state, setState] = useState(() => readFromStorage());

  // Cross-tab sync: if another tab updates the status, mirror it here
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        setState(readFromStorage());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const publishBalance = useCallback(({ isBalanced, difference, periodId }) => {
    const next = {
      isBalanced: !!isBalanced,
      difference: Number(difference) || 0,
      periodId: periodId || null,
      updatedAt: new Date().toISOString(),
    };
    setState(next);
    writeToStorage(next);
  }, []);

  const clearBalance = useCallback(() => {
    setState(defaultState);
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
  }, []);

  return (
    <BalanceSheetContext.Provider value={{ ...state, publishBalance, clearBalance }}>
      {children}
    </BalanceSheetContext.Provider>
  );
};

export const useBalanceSheet = () => useContext(BalanceSheetContext);
